'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useEffectOnce } from '@endurancewarranty/ui';
import { useCenterDialog } from '@hooks/dialogs/useCenterDialog';
import { useIsPopupWindow } from '@hooks/useIsPopupWindow';
import { UrlBuilder } from '@lib/utils/UrlBuilder';
import { SaveInProgressContent } from '@quotes/[quoteId]/_components/SaveInProgressContent';
import { submittingNavigationAtom } from '@states/form/submittingNavigationAtom';
import { useJotaiValue } from '@states/jotai/hooks';
import type { AppRouteEnum } from '@lib/enums/AppRouteEnum';
import type { TPopupMessage } from '@lib/types/TPopupMessage';
import type { AppRouterInstance, NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
export type TAppRouter = AppRouterInstance & {
  build: (route: AppRouteEnum) => UrlBuilder;
};
type TNavigationOverrides = Pick<AppRouterInstance, 'back' | 'forward' | 'refresh' | 'push' | 'replace'>;

/**
 * Wrapper around the Next `useRouter` hook that
 * allows building type safe routes
 */
export function useAppRouter(config) {
  const $ = _c(28);
  const {
    isSubmitting
  } = useJotaiValue(submittingNavigationAtom);
  const isPopup = useIsPopupWindow();
  const {
    open,
    close
  } = useCenterDialog();
  const router = useRouter();
  let t0;
  if ($[0] !== close || $[1] !== open) {
    t0 = () => open(<SaveInProgressContent onClose={close} />);
    $[0] = close;
    $[1] = open;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const openWarning = t0;
  let t1;
  let t2;
  if ($[3] !== openWarning) {
    t1 = (_href, _options) => {
      openWarning();
    };
    t2 = (_href_0, _options_0) => {
      openWarning();
    };
    $[3] = openWarning;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== openWarning || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      back: openWarning,
      forward: openWarning,
      refresh: openWarning,
      push: t1,
      replace: t2
    };
    $[6] = openWarning;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const blockedRouter = t3;
  const postToOpener = _temp;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      back: _temp2,
      forward: _temp3,
      refresh: _temp4,
      push: postToOpener,
      replace: postToOpener
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const popupRouter = t4;
  let t5;
  if ($[11] !== isPopup) {
    t5 = isPopup ? popupRouter : {};
    $[11] = isPopup;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== blockedRouter || $[14] !== isSubmitting) {
    t6 = isSubmitting ? blockedRouter : {};
    $[13] = blockedRouter;
    $[14] = isSubmitting;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== router || $[17] !== t5 || $[18] !== t6) {
    t7 = {
      ...router,
      ...t5,
      ...t6
    };
    $[16] = router;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const appRouter = t7;
  let t8;
  if ($[20] !== config?.prefetchRoutes || $[21] !== router) {
    t8 = () => {
      config?.prefetchRoutes?.forEach(route => router.prefetch(route));
    };
    $[20] = config?.prefetchRoutes;
    $[21] = router;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  useEffectOnce(t8);
  let t9;
  if ($[23] !== appRouter) {
    t9 = route_0 => new UrlBuilder(appRouter, route_0);
    $[23] = appRouter;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== appRouter || $[26] !== t9) {
    t10 = {
      ...appRouter,
      build: t9
    };
    $[25] = appRouter;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function _temp4() {
  return window.location.reload();
}
function _temp3() {}
function _temp2() {}
function _temp(href) {
  const opener = window.opener as Nullable<Window>;
  if (opener !== null && !opener.closed) {
    opener.postMessage({
      type: "navigate",
      href
    } satisfies TPopupMessage, window.location.origin);
    opener.focus();
    return;
  }
  window.open(href, "_blank");
}